import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ProfileAvatar } from "../components/ObserverProfileBits";
import ObserverSponsoredSlot from "../components/ObserverSponsoredSlot";
import SeoHead from "../components/SeoHead";
import { observerApi } from "../services/observerApi";
import { ObserverDirectoryPage as DirectoryResponse } from "../types/observer";

const origin = "https://observertheory.com";

const pageLink = (path: string, page: number) =>
  page > 1 ? `${path}?page=${page}` : path;

export default function ObserverDirectoryPage() {
  const { category: categorySlug = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const requestedPage = Math.max(1, Number(params.get("page")) || 1);
  const sort = (["most_established", "recently_added", "a_z"].includes(params.get("sort") || "") ? params.get("sort") : "most_established") as "most_established" | "recently_added" | "a_z";
  const [directory, setDirectory] = useState<DirectoryResponse>();
  const [error, setError] = useState("");

  useEffect(() => {
    setDirectory(undefined);
    setError("");
    observerApi
      .directory(categorySlug, requestedPage, 24, sort)
      .then(setDirectory)
      .catch((requestError) =>
        setError(
          requestError?.response?.status === 404
            ? "This practice category does not exist."
            : "The practitioner directory is temporarily unavailable.",
        ),
      );
  }, [categorySlug, requestedPage, sort]);

  const path = categorySlug
    ? `/practitioners/${encodeURIComponent(categorySlug)}`
    : "/practitioners";
  const canonicalPath = pageLink(path, requestedPage);
  const title = directory?.category
    ? `${directory.category.name} | Observer Theory`
    : "Browse Spiritual Practitioners by Practice | Observer Theory";
  const description = directory?.category
    ? directory.category.description
    : "Browse automatically published, sourced factual records for manifestation coaches, tarot readers, psychics, mediums, astrologers, energy practitioners, and spiritual teachers.";
  const pageOutOfRange = Boolean(
    directory &&
      directory.totalPages > 0 &&
      requestedPage > directory.totalPages,
  );
  const noIndex = Boolean(error || pageOutOfRange || directory?.total === 0);

  const jsonLd = useMemo(() => {
    if (!directory || noIndex) return undefined;
    const breadcrumbItems = [
      {
        "@type": "ListItem",
        position: 1,
        name: "Practitioners",
        item: origin + "/practitioners",
      },
    ];
    if (directory.category) {
      breadcrumbItems.push({
        "@type": "ListItem",
        position: 2,
        name: directory.category.name,
        item: origin + path,
      });
    }
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          name: title,
          description,
          url: origin + canonicalPath,
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: directory.items.length,
            itemListOrder: "https://schema.org/ItemListOrderAscending",
            itemListElement: directory.items.map((row, index) => ({
              "@type": "ListItem",
              position: (requestedPage - 1) * directory.pageSize + index + 1,
              name: row.entity.displayName,
              url: `${origin}/profile/${row.entity.publicId}`,
            })),
          },
        },
        { "@type": "BreadcrumbList", itemListElement: breadcrumbItems },
      ],
    };
  }, [canonicalPath, description, directory, noIndex, path, requestedPage, title]);

  const pages = useMemo(() => {
    if (!directory || directory.totalPages < 2) return [];
    const values = new Set<number>([1, directory.totalPages]);
    for (
      let page = Math.max(1, requestedPage - 2);
      page <= Math.min(directory.totalPages, requestedPage + 2);
      page++
    ) {
      values.add(page);
    }
    return Array.from(values).sort((a, b) => a - b);
  }, [directory, requestedPage]);

  return (
    <div className="ot-page ot-directory">
      <SeoHead
        title={title}
        description={description}
        path={canonicalPath}
        noIndex={noIndex}
        jsonLd={jsonLd}
      />
      <nav className="ot-breadcrumbs" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden="true">/</span>
        {directory?.category ? (
          <>
            <Link to="/practitioners">Practitioners</Link>
            <span aria-hidden="true">/</span>
            <span>{directory.category.shortName}</span>
          </>
        ) : (
          <span>Practitioners</span>
        )}
      </nav>
      <ObserverSponsoredSlot />
      <header className="ot-page-head">
        <span className="section-kicker">Browse the public record</span>
        <h1>
          {directory?.category?.name || "Spiritual practitioners by practice"}
        </h1>
        <p>{description}</p>
        <p className="ot-order-note">
          The labeled sort uses sourced activity only when selected. It is not a recommendation or quality score. Payment, claim status, review sentiment, and transparency observations never affect organic order.
        </p>
        <label className="ot-sort">Sort <select value={sort} onChange={(event) => { const next = new URLSearchParams(params); next.set("sort", event.target.value); next.delete("page"); setParams(next); }}><option value="most_established">Most Established / Most Active</option><option value="recently_added">Recently added</option><option value="a_z">A–Z</option></select></label>
      </header>

      <nav className="ot-category-nav" aria-label="Practice categories">
        <Link className={!categorySlug ? "active" : ""} to="/practitioners">
          All published records
          {directory ? <span>{directory.publishedTotal}</span> : null}
        </Link>
        {directory?.categories.map((category) => (
          <Link
            className={category.slug === categorySlug ? "active" : ""}
            key={category.slug}
            to={`/practitioners/${category.slug}`}
          >
            {category.shortName}
            <span>{category.count}</span>
          </Link>
        ))}
      </nav>

      {!directory && !error && <p>Loading published records…</p>}
      {error && (
        <div className="ot-empty">
          <h2>Directory unavailable</h2>
          <p>{error}</p>
          <Link className="button button-ink" to="/practitioners">
            Browse all practitioners
          </Link>
        </div>
      )}
      {directory && directory.total === 0 && (
        <div className="ot-empty">
          <h2>No published records in this category yet</h2>
          <p>
            Candidates publish automatically when factual identity, public commercial work, sources, minimum-record, and safety checks pass. Lower-confidence categories remain available by exact-name search while they await sampling audit.
          </p>
          <Link className="button button-ink" to="/report?kind=nomination">
            Nominate a practitioner
          </Link>
        </div>
      )}
      {pageOutOfRange && (
        <div className="ot-empty">
          <h2>This directory page is empty</h2>
          <Link className="button button-ink" to={path}>
            Return to the first page
          </Link>
        </div>
      )}
      {directory && !pageOutOfRange && directory.items.length > 0 && (
        <>
          <div className="ot-directory-summary">
            <p>
              {directory.total} published {directory.total === 1 ? "record" : "records"}
            </p>
            <p>
              Page {directory.page} of {directory.totalPages}
            </p>
          </div>
          <div className="ot-results ot-directory-results">
            {directory.items.map((row) => (
              <Link
                className="ot-result"
                key={row.entity.publicId}
                to={`/profile/${row.entity.publicId}`}
              >
                <ProfileAvatar
                  entity={row.entity}
                  size="small"
                  linkToSource={false}
                />
                <span>
                  {row.entity.entityType} · {row.entity.claimState === "claimed" ? "Claimed" : "Unclaimed"}
                </span>
                <h2>{row.entity.displayName}</h2>
                <p>{row.entity.practiceTags.join(" · ")}</p>
                <small>
                  {row.why}. {row.recordFacts} sourced record facts;{" "}
                  {row.verifiedReviewDepth} verified reviews.
                </small>
              </Link>
            ))}
          </div>
          {pages.length > 1 && (
            <nav className="ot-pagination" aria-label="Directory pages">
              {requestedPage > 1 && (
                <Link to={pageLink(path, requestedPage - 1)}>Previous</Link>
              )}
              {pages.map((page, index) => (
                <React.Fragment key={page}>
                  {index > 0 && pages[index - 1] !== page - 1 && (
                    <span aria-hidden="true">…</span>
                  )}
                  <Link
                    aria-current={page === requestedPage ? "page" : undefined}
                    className={page === requestedPage ? "active" : ""}
                    to={pageLink(path, page)}
                  >
                    {page}
                  </Link>
                </React.Fragment>
              ))}
              {requestedPage < directory.totalPages && (
                <Link to={pageLink(path, requestedPage + 1)}>Next</Link>
              )}
            </nav>
          )}
        </>
      )}
    </div>
  );
}
